import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  BaseTextField,
  Card,
  ControlledRadioGroup,
  ControlledTextField,
} from '../../../../shared/components';
import { calculateDrawings } from '../../../domain/typeOfWork.calculations';
import {
  WORK_AGE_OPTIONS,
  WORK_CATEGORY,
  WORK_CATEGORY_OPTIONS,
} from '../../../domain/work.constants';
import { LockedJobTypes } from '../../../domain/workJobLookups';
import AlliedWorkSection from '../components/AlliedWorkSection';
import BuildingWorkSection, { TotalAreaField } from '../components/BuildingWorkSection';

const isAlliedCategory = (workCategory) =>
  workCategory === WORK_CATEGORY.ALLIED || workCategory === WORK_CATEGORY.ALLIED_BUILDING;

const isBuildingCategory = (workCategory) =>
  workCategory === WORK_CATEGORY.BUILDING || workCategory === WORK_CATEGORY.ALLIED_BUILDING;

/**
 * `lockedTypes` switches the step from "choose the work type" to "this is the work type".
 *
 * When the panel is opened for an existing job, the project type, construction work type
 * and building type are already recorded on `work_jobs` and are edited on the Edit Job
 * screen itself. Offering them again here as radios would let the two screens disagree,
 * so they are shown as labels read straight off the job.
 *
 * `title` heads the card. The wizard leaves it at "Type of Work" — the step where that
 * choice is made — while the Work Details panel on an existing job passes its own name.
 */
export default function StepTypeOfWork({
  lockedTypes,
  title = 'Type of Work',
}: { lockedTypes?: LockedJobTypes; title?: string } = {}) {
  const { watch, setValue, getValues, resetField } = useFormContext();

  const workAge = watch('workAge');
  const workCategory = watch('workCategory');
  const isLocked = Boolean(lockedTypes);

  const watchedValues = watch([
    'workAge',
    'workCategory',
    'hasBasement',
    'basementCount',
    'groundLevelType',
    'singleGroundUsage',
    'singleHasMezzanine',
    'lowerGroundUsage',
    'lowerGroundHasMezzanine',
    'upperGroundUsage',
    'upperGroundHasMezzanine',
    'upperFloorsCount',
    'workCompletedUpto',
    'hasStaircaseOhwt',
    'staircaseOhwtCount',
    'slabsCount',
    'lowerGroundRequiresColumnFooting',
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const drawings = useMemo(() => calculateDrawings(getValues()), [getValues, watchedValues]);

  /**
   * Keep "No. of slabs" — and with it the Structural Drawings — in step with the floors.
   *
   * Whenever a floor answer changes the count (basement yes/no or how many, ground level
   * type, usage or mezzanine, upper floors, staircap / OHWT / LMR), the box is set to the
   * new count at once, whether that adds slabs or takes them away. A number typed by hand
   * stands only until the floors change again.
   *
   * On opening, a saved count is kept as long as it is not below what the floors need —
   * reopening a job must not quietly rewrite a figure someone deliberately raised.
   */
  const followedMinSlabs = useRef<number | null>(null);

  useEffect(() => {
    const previous = followedMinSlabs.current;
    followedMinSlabs.current = drawings.minSlabs;

    if (previous === drawings.minSlabs) return;

    if (previous === null) {
      const current = getValues('slabsCount');
      const keepSaved =
        current !== '' && current != null && Number(current) >= drawings.minSlabs;
      if (keepSaved) return;
    }

    setValue('slabsCount', drawings.minSlabs, { shouldDirty: true, shouldValidate: true });
  }, [drawings.minSlabs, getValues, setValue]);

  // Changing "new vs existing" invalidates the work category chosen under it. When both
  // come from the job they are seeded together and neither is editable, so clearing here
  // would wipe the seeded category on the first render.
  useEffect(() => {
    if (isLocked || !workAge) return;
    resetField('workCategory');
  }, [workAge, resetField, isLocked]);

  useEffect(() => {
    setValue('hasAlliedWork', isAlliedCategory(workCategory), {
      shouldDirty: false,
      shouldValidate: false,
    });

    setValue('hasBuildingWork', isBuildingCategory(workCategory), {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [workCategory, setValue]);

  useEffect(() => {
    const isAllied = isAlliedCategory(workCategory);
    if (!isAllied) resetField('details.alliedWorkDetails');
  }, [workCategory, resetField]);

  useEffect(() => {
    const isBuilding = isBuildingCategory(workCategory);
    if (!isBuilding) {
      resetField('buildingType');
      resetField('buildingTypeOther');
      resetField('hasBasement');
      resetField('basementCount');
      resetField('groundLevelType');
      resetField('singleGroundUsage');
      resetField('singleHasMezzanine');
      resetField('lowerGroundRequiresColumnFooting');
      resetField('lowerGroundUsage');
      resetField('lowerGroundHasMezzanine');
      resetField('upperGroundUsage');
      resetField('upperGroundHasMezzanine');
      resetField('upperFloorsCount');
    }
  }, [workCategory, resetField]);

  return (
    <Card title={title}>
      {lockedTypes ? (
        <LockedTypeSummary types={lockedTypes} />
      ) : (
        <>
          <ControlledRadioGroup
            name='workAge'
            options={WORK_AGE_OPTIONS}
            wrapperOrientation='row'
            rules={{ required: 'Please select whether this is a new or existing project' }}
          />

          <ControlledRadioGroup
            name='workCategory'
            options={WORK_CATEGORY_OPTIONS}
            wrapperOrientation='row'
            disabled={!workAge}
            rules={{ required: workAge ? 'Please select work category' : false }}
          />
        </>
      )}

      {workCategory === WORK_CATEGORY.ALLIED && <AlliedWorkSection />}
      {workCategory === WORK_CATEGORY.BUILDING && (
        <BuildingWorkSection drawings={drawings} lockedBuildingType={lockedTypes?.buildingType} />
      )}
      {workCategory === WORK_CATEGORY.ALLIED_BUILDING && (
        <BuildingWorkSection
          isAllied
          drawings={drawings}
          lockedBuildingType={lockedTypes?.buildingType}
        />
      )}
      {!!workCategory && <TotalAreaField numbered={isBuildingCategory(workCategory)} />}
      {!!workCategory && <DrawingCountRow drawings={drawings} />}
      {!!workCategory && (
        <ControlledTextField
          id='typeOfWorkNotes'
          name='typeOfWorkNotes'
          label='Notes'
          placeholder='Add any internal notes for this step (optional)'
          isMultiline
          rows={4}
          rules={{
            maxLength: { value: 1000, message: 'Notes cannot exceed 1000 characters' },
          }}
          showCharCount
        />
      )}
    </Card>
  );
}

/**
 * The job's own Type of Work and Building Type, as stored on `work_jobs`.
 *
 * Read-only on purpose — these three are edited on the Edit Job screen that opens this
 * panel, and this panel's save writes `job_floors` only.
 */
function LockedTypeSummary({ types }: { types: LockedJobTypes }) {
  const rows = [
    { label: 'Project Type', value: types.projectType },
    { label: 'Type of Work', value: types.constructionWorkType },
    { label: 'Building Type', value: types.buildingType },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
        columnGap: 4,
        rowGap: 1.5,
      }}
    >
      {rows.map(({ label, value }) => (
        <Box key={label}>
          <Typography variant='caption' color='text.secondary'>
            {label}
          </Typography>
          <Typography variant='body1' color={value ? 'text.primary' : 'text.disabled'}>
            {value || 'Not set'}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function DrawingCountRow({ drawings }) {
  return (
    <div className={`row gap-5 ${drawings.columnFootingDrawings > 0 ? 'row--between' : ''}`}>
      {drawings.columnFootingDrawings > 0 && (
        <div className='row gap-3'>
          <Typography variant='body1' sx={{ whiteSpace: 'nowrap' }}>
            Column-Footing drawings:
          </Typography>
          <BaseTextField
          id="step-field-1"
          onChange={() => {}}
            suffix={drawings.columnRange}
            value={drawings.columnFootingDrawings}
            readOnly
          />
        </div>
      )}
      <div className='row gap-3'>
        <Typography variant='body1' sx={{ whiteSpace: 'nowrap' }}>
          No. of slabs:
        </Typography>
        <ControlledTextField
          id='slabsCount'
          name='slabsCount'
          type='number'
          rules={{
            min: {
              value: drawings.minSlabs,
              message: `Minimum ${drawings.minSlabs} slabs required`,
            },
            validate: (v) => v === '' || Number(v) >= 0 || 'Slabs cannot be negative',
          }}
          suffix={drawings.slabRange}
          hideArrow
        />
      </div>
      <div className='row gap-3'>
        <Typography variant='body1' sx={{ whiteSpace: 'nowrap' }}>
          Structural Drawings
        </Typography>
        <span className='text--secondary'>{drawings.structuralDrawings} Drawings</span>
      </div>
    </div>
  );
}
